import uniqueId from 'lodash/uniqueId';
import stringify from 'safe-stable-stringify';
import { match, P } from 'ts-pattern';
import { z } from 'zod';
import type { RequestContainer } from '$lib/container/container.server';
import { equalOrIsNull } from '$lib/kysely/kysely-utils.js';
import { allValues } from '$lib/utils/promise.js';
import { stripNullableValues } from '$lib/utils/stripNullableValues.js';
import type { PartialOnUndefined } from '$lib/utils/types.js';
import type {
    CurrentParticipantInput,
    QueryParticipantArgs,
    QueryParticipantOrNullArgs,
    QueryParticipantsArgs,
    QuerySearchParticipantsForSelectArgs,
} from '$gql/resolver-types.js';
import type { ParticipantInput } from '$gql/resolver-types.js';
import { CompanyRef } from '$gql/resolvers/CompanyRef.js';
import { UserRef } from '$gql/resolvers/UserRef.js';
import { myThreadQuery } from '$gql/resolvers/inbox/queries.js';

export type ParticipantRef = UserRef | CompanyRef;

export const zParticipantInput = z.object({
    userId: z.number().nullish(),
    companyId: z.number().nullish(),
});

export function participantRefAreEqual(p1: ParticipantRef, p2: ParticipantRef): boolean {
    return match([p1, p2])
        .with([P.instanceOf(UserRef), P.instanceOf(UserRef)], ([p1, p2]) => {
            return p1.id() === p2.id();
        })
        .with([P.instanceOf(CompanyRef), P.instanceOf(CompanyRef)], ([p1, p2]) => {
            return p1.id() === p2.id();
        })
        .otherwise(() => false);
}

export function participantInputAreEqual(
    p1: ParticipantInput,
    p2: ParticipantInput,
): boolean {
    const p1Company = p1.companyId ?? null;
    const p2Company = p2.companyId ?? null;
    const p1User = p1.userId ?? null;
    const p2User = p2.userId ?? null;

    return p1Company === p2Company && p1User === p2User;
}

export function matchParticipantInput<A, B = A>(
    p: PartialOnUndefined<ParticipantInput>,
    callbacks: { user: (id: number) => A; company: (id: number) => B },
): A | B {
    return match(p)
        .with({ userId: P.number }, ({ userId }) => callbacks.user(userId))
        .with({ companyId: P.number }, ({ companyId }) => callbacks.company(companyId))
        .run();
}

export function matchCurrentParticipantInput<A, B = A, C = A>(
    p: PartialOnUndefined<CurrentParticipantInput>,
    callbacks: {
        user: (id: number) => A;
        company: (id: number) => B;
        all: (all: boolean) => C;
    },
): A | B | C {
    return match(p)
        .with({ userId: P.number }, ({ userId }) => callbacks.user(userId))
        .with({ companyId: P.number }, ({ companyId }) => callbacks.company(companyId))
        .with({ all: P.boolean }, ({ all }) => callbacks.all(all))
        .run();
}

export function participantInputToRef(
    p: PartialOnUndefined<ParticipantInput>,
    context: Pick<RequestContainer, 'refs'>,
): ParticipantRef {
    return matchParticipantInput(p, {
        user: (id) => context.refs().user(id),
        company: (id) => context.refs().company(id),
    });
}

export function matchParticipantRef<T>(
    p: ParticipantRef,
    callbacks: { user: (user: UserRef) => T; company: (company: CompanyRef) => T },
): T {
    return match(p)
        .with(P.instanceOf(UserRef), (user) => callbacks.user(user))
        .with(P.instanceOf(CompanyRef), (company) => callbacks.company(company))
        .exhaustive();
}

export function participantRefToInput(p: ParticipantRef): ParticipantInput {
    return matchParticipantRef<ParticipantInput>(p, {
        user: (user) => ({ userId: user.id(), companyId: null }),
        company: (company) => ({ userId: null, companyId: company.id() }),
    });
}

export function participantRefToString(p: ParticipantRef): string {
    return `${p.__typename}:${p.id()}`;
}

export function stringifyParticipantInput(p: ParticipantInput): string {
    return (
        stringify(
            stripNullableValues({
                userId: p.userId ?? null,
                companyId: p.companyId ?? null,
            }),
        ) ?? ''
    );
}

const ID = uniqueId('ParticipantRef');

export async function inboxNbUnreadResolve(
    context: Pick<RequestContainer, 'db' | 'dataLoaderRegistry' | 'refs'>,
    ref: UserRef | CompanyRef,
) {
    const { db, dataLoaderRegistry } = context;

    const loader = dataLoaderRegistry().createForRequest(
        `${ID}-inboxNbUnreadResolve`,
        async (participants: readonly ParticipantInput[]) => {
            return Promise.all(
                participants.map(async (participant) => {
                    const { userId = null, companyId = null } = participant;

                    const query = db()
                        .with('my_thread(thread_id)', (db) =>
                            db
                                .selectFrom(myThreadQuery(ref, db).as('tp'))
                                .select(['thread_id']),
                        )
                        .with('my_thread_not_deleted(thread_id)', (db) =>
                            db
                                .selectFrom('my_thread')
                                .select('my_thread.thread_id')
                                .innerJoin('inbox_thread_metadata as meta', (join) =>
                                    join
                                        .onRef(
                                            'meta.thread_id',
                                            '=',
                                            'my_thread.thread_id',
                                        )
                                        .on(({ and, ref }) =>
                                            and([
                                                equalOrIsNull(
                                                    ref('meta.user_target_id'),
                                                    userId,
                                                ),
                                                equalOrIsNull(
                                                    ref('meta.company_target_id'),
                                                    companyId,
                                                ),
                                            ]),
                                        ),
                                )
                                .where(({ ref, and, eb }) =>
                                    and([eb(ref('meta.deleted_date'), 'is', null)]),
                                ),
                        )
                        .with(
                            'my_thread_messages_not_deleted(thread_id, message_id)',
                            (db) =>
                                db
                                    .selectFrom('my_thread_not_deleted')
                                    .innerJoin(
                                        'inbox_message as m',
                                        'm.thread_id',
                                        'my_thread_not_deleted.thread_id',
                                    )
                                    .select((_eb) => [
                                        'm.thread_id as thread_id',
                                        'm.id as message_id',
                                    ])
                                    .leftJoin('inbox_message_metadata as meta', (join) =>
                                        join
                                            .onRef('meta.message_id', '=', 'm.id')
                                            .on(({ and, ref }) =>
                                                and([
                                                    equalOrIsNull(
                                                        ref('meta.user_target_id'),
                                                        userId,
                                                    ),
                                                    equalOrIsNull(
                                                        ref('meta.company_target_id'),
                                                        companyId,
                                                    ),
                                                ]),
                                            ),
                                    )
                                    .where(({ ref, and, eb }) =>
                                        and([eb(ref('meta.deleted_date'), 'is', null)]),
                                    ),
                        )
                        .with(
                            'nb_messages_unread_for_my_threads(thread_id, count)',
                            (db) =>
                                db
                                    .selectFrom('my_thread_messages_not_deleted as tm')
                                    .select((eb) => [
                                        'tm.thread_id as thread_id',
                                        eb.fn.count('tm.message_id').as('count'),
                                    ])
                                    .leftJoin('inbox_message_metadata as meta', (join) =>
                                        join
                                            .onRef(
                                                'meta.message_id',
                                                '=',
                                                'tm.message_id',
                                            )
                                            .on(({ and, ref }) =>
                                                and([
                                                    equalOrIsNull(
                                                        ref('meta.user_target_id'),
                                                        userId,
                                                    ),
                                                    equalOrIsNull(
                                                        ref('meta.company_target_id'),
                                                        companyId,
                                                    ),
                                                ]),
                                            ),
                                    )
                                    .where(({ ref, eb }) =>
                                        eb(ref('meta.read_date'), 'is', null),
                                    )
                                    .groupBy('tm.thread_id'),
                        )
                        .selectFrom('nb_messages_unread_for_my_threads')
                        .where('nb_messages_unread_for_my_threads.count', '>=', 1)
                        .select((eb) => eb.fn.countAll().as('count'));

                    const row = await query.executeTakeFirst();
                    return parseInt((row?.count ?? 0).toString());
                }),
            );
        },
        {
            cacheKeyFn: (key) => stringify(key),
        },
    );

    return await loader.load(participantRefToInput(ref));
}

export class QueryParticipantRef {
    constructor(private readonly _context: Pick<RequestContainer, 'refs' | 'db'>) {}

    async participant(this: this, args: PartialOnUndefined<QueryParticipantArgs>) {
        return match(args.participant)
            .with({ userId: P.number }, ({ userId }) => this._context.refs().user(userId))
            .with({ companyId: P.number }, ({ companyId }) =>
                this._context.refs().company(companyId),
            )
            .otherwise(() => {
                throw new Error('Participant is invalid.');
            });
    }

    async participantOrNull(
        this: this,
        args: PartialOnUndefined<QueryParticipantOrNullArgs>,
    ) {
        return args.participant == null
            ? null
            : this.participant({ participant: args.participant });
    }

    async participants(this: this, args: PartialOnUndefined<QueryParticipantsArgs>) {
        return args.participants.map((p) => this.participant({ participant: p }));
    }

    async searchParticipantsForSelect(
        this: this,
        args: PartialOnUndefined<QuerySearchParticipantsForSelectArgs>,
    ): Promise<readonly ParticipantRef[]> {
        const { refs, db } = this._context;

        if (args.managedByOrManagersOf != null) {
            const managedByOrManagersOf = participantInputToRef(
                args.managedByOrManagersOf,
                this._context,
            );
            return await matchParticipantRef<Promise<readonly ParticipantRef[]>>(
                managedByOrManagersOf,
                {
                    company: async (company) => {
                        const managers = await company.managers();
                        return Promise.all(managers.map((m) => m.user()));
                    },
                    user: async (user) => {
                        const managers = await user.managedCompanies();
                        return Promise.all(managers.map((m) => m.company()));
                    },
                },
            );
        }

        const userRowsPromise = db()
            .selectFrom('user')
            .where((eb) =>
                eb.or([
                    eb(
                        eb.fn('concat', [
                            eb.ref('user.firstname'),
                            eb.val(' '),
                            eb.ref('user.lastname'),
                        ]),
                        'like',
                        `${args.q}%`,
                    ),
                    eb(
                        eb.fn('concat', [
                            eb.ref('user.lastname'),
                            eb.val(' '),
                            eb.ref('user.firstname'),
                        ]),
                        'like',
                        `${args.q}%`,
                    ),
                ]),
            )
            .limit(20)
            .select('user.id')
            .execute();

        const companyRowsPromise = db()
            .selectFrom('company')
            .where('online', '=', 1)
            .where('valid', '=', 1)
            .where('name', 'like', `${args.q}%`)
            .limit(20)
            .select('company.id')
            .execute();

        const { userRows, companyRows } = await allValues({
            userRows: userRowsPromise,
            companyRows: companyRowsPromise,
        });

        return [
            ...userRows.map((r) => refs().user(r.id)),
            ...companyRows.map((r) => refs().company(r.id)),
        ];
    }
}
